import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  useTheme,
  alpha,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Add as AddIcon,
  Calculate as CalculateIcon,
  FileDownload as ExportIcon,
  CheckCircle as CheckIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';

const Liquidaciones = () => {
  const theme = useTheme();
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);

  const liquidacionesData = [
    { id: 1, periodo: 'Enero 2026', empleados: 284, totalBruto: 42500000, totalNeto: 35200000, estado: 'Pagada', fechaPago: '05/02/2026' },
    { id: 2, periodo: 'Diciembre 2025', empleados: 280, totalBruto: 65800000, totalNeto: 54500000, estado: 'Pagada', fechaPago: '22/12/2025' },
    { id: 3, periodo: 'SAC 2do Sem 2025', empleados: 280, totalBruto: 21250000, totalNeto: 17600000, estado: 'Pagada', fechaPago: '18/12/2025' },
    { id: 4, periodo: 'Noviembre 2025', empleados: 278, totalBruto: 41200000, totalNeto: 34100000, estado: 'Pagada', fechaPago: '05/12/2025' },
  ];

  const detalleEmpleados = [
    { legajo: 'PL-001', nombre: 'Juan Rodríguez', basico: 850000, adicionales: 425000, deducciones: 210000, neto: 1065000 },
    { legajo: 'PL-002', nombre: 'Pedro García', basico: 920000, adicionales: 368000, deducciones: 238000, neto: 1050000 },
    { legajo: 'PL-003', nombre: 'María López', basico: 680000, adicionales: 136000, deducciones: 155000, neto: 661000 },
    { legajo: 'PL-004', nombre: 'Carlos Mendoza', basico: 780000, adicionales: 312000, deducciones: 208000, neto: 884000 },
  ];

  const resumenStats = [
    { label: 'Total Bruto', value: '$42.5M', color: theme.palette.primary.main },
    { label: 'Aportes y Contrib.', value: '$7.3M', color: theme.palette.warning.main },
    { label: 'Total Neto', value: '$35.2M', color: theme.palette.success.main },
    { label: 'Empleados', value: '284', color: theme.palette.info.main },
  ];

  return (
    <Box className="fade-in">
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} color="primary">
            Liquidación de Sueldos
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Gestión de liquidaciones y recibos de haberes
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="outlined" startIcon={<ExportIcon />}>
            Exportar
          </Button>
          <Button variant="contained" startIcon={<CalculateIcon />} onClick={() => setOpenDialog(true)}>
            Nueva Liquidación
          </Button>
        </Box>
      </Box>

      {/* Resumen Enero 2026 */}
      <Card sx={{ mb: 4, bgcolor: alpha(theme.palette.primary.main, 0.02) }}>
        <CardContent>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Resumen Liquidación Enero 2026
          </Typography>
          <Grid container spacing={3}>
            {resumenStats.map((stat, index) => (
              <Grid item xs={6} sm={3} key={index}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" fontWeight={700} color={stat.color}>
                    {stat.value}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {stat.label}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        {/* Histórico de Liquidaciones */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Histórico de Liquidaciones
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Período</TableCell>
                      <TableCell align="right">Total Neto</TableCell>
                      <TableCell>Estado</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {liquidacionesData.map((row) => (
                      <TableRow key={row.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>{row.periodo}</Typography>
                          <Typography variant="caption" color="text.secondary">{row.empleados} empleados</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={600}>
                            ${(row.totalNeto / 1000000).toFixed(1)}M
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={row.estado === 'Pagada' ? <CheckIcon /> : <ScheduleIcon />}
                            label={row.estado}
                            size="small"
                            color={row.estado === 'Pagada' ? 'success' : 'warning'}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Detalle por Empleado */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Detalle por Empleado - Enero 2026
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Empleado</TableCell>
                      <TableCell align="right">Básico</TableCell>
                      <TableCell align="right">Neto</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detalleEmpleados.map((row) => (
                      <TableRow key={row.legajo} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar sx={{ width: 28, height: 28, bgcolor: theme.palette.primary.main, fontSize: '0.7rem' }}>
                              {row.nombre.split(' ').map(n => n[0]).join('')}
                            </Avatar>
                            <Box>
                              <Typography variant="body2">{row.nombre}</Typography>
                              <Typography variant="caption" color="text.secondary">{row.legajo}</Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell align="right">${row.basico.toLocaleString()}</TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={600} color="success.main">
                            ${row.neto.toLocaleString()}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nueva Liquidación</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Tipo de Liquidación</InputLabel>
                <Select label="Tipo de Liquidación">
                  <MenuItem value="mensual">Mensual</MenuItem>
                  <MenuItem value="sac">SAC (Aguinaldo)</MenuItem>
                  <MenuItem value="vacaciones">Vacaciones</MenuItem>
                  <MenuItem value="final">Liquidación Final</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Período</InputLabel>
                <Select label="Período">
                  <MenuItem value="02-2026">Febrero 2026</MenuItem>
                  <MenuItem value="01-2026">Enero 2026</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Empleados</InputLabel>
                <Select label="Empleados">
                  <MenuItem value="todos">Todos (284)</MenuItem>
                  <MenuItem value="campo">Personal de Campo (180)</MenuItem>
                  <MenuItem value="admin">Administrativos (45)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Fecha de Pago"
                type="date"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={() => setOpenDialog(false)}>
            Generar Liquidación
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Liquidaciones;
